import { withTheme } from '@emotion/react';
import underConstruction from 'assets/illustrations/Empty-box-disabled.png';
import deepEqual from 'deep-equal';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import compose from 'utils/compose';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { StyledEngineProvider } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';

import navIconSettings from 'assets/icons/nav-icons/settings.inline.svg';
import navIconCategories from 'assets/icons/settings-menu/categories.tags.settings.simplifi.inline.svg';
import navIconTags from 'assets/icons/tags.svg';
import { v4 as uuidv4 } from 'uuid';

import QDialogs from 'components/QDialogs';
import QList from 'components/QList';
import QListDetails from 'components/QList/QListDetails';

import FinancesSettingsSimplifiIcon from 'assets/icons/settings-menu/dataset.inline.svg';

const icons = {
  categories: <img alt={'categories'} src={navIconCategories} />,
  tags: <img alt={'categories'} src={navIconTags} />,
  settings: <img alt={'categories'} src={navIconSettings} />,
  finances: <FinancesSettingsSimplifiIcon />,
};
const SectionTitle = styled.div`
    padding-bottom: 20px;
    padding-top: 30px;
`;

const styles = (theme) => ({
  root: {
    minWidth: theme.defaults.content.pageMinWidth,
  },
  sectionTitle: {
    marginBottom: 12,
  },
  paperRoot: {
    margin: '0 auto',
  },
  listItem: {
    paddingLeft: 20,
  },
  listItemText: {
    fontSize: 13,
  },
  selects: {
    marginRight: 16,
  },
  underConstructionContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 271,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navMenuItem: {
    display: 'inline-block',
    '& img': {
      width: 35,
      display: 'inline-block',
      verticalAlign: 'middle',
    },
  },
  titleText: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: 5,
    fontWeight: 200,
    color: theme.palette.text.primary,
  },
});

class SettingsItems extends PureComponent {
  // eslint-disable-line

  constructor(props) {
    super(props);

    props.getItems();

    this.state = {
      data: this.props.mkItemArray(props.items),
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.items !== this.props.items) {
      this.setState({ data: nextProps.mkItemArray(nextProps.items) });
    }
  }

  findIdInData = (id, data) => {
    let retVal = null;

    data.some((x) => {
      if (x.id === id) {
        retVal = x;
        return true;
      }
      if (x.children) {
        retVal = this.findIdInData(id, x.children);
        return retVal !== null;
      }
      return false;
    });
    return retVal;
  };

  executeDeleteItems = (items) => {
    if (this.props.deleteItems) {
      const itemsToDelete = [];
      items.forEach((id) => {
        const item = this.findIdInData(id, this.state.data);
        if (item) itemsToDelete.push(this.props.mkImmutableItem(item));
      });
      return this.props.deleteItems(itemsToDelete);
    }

    items.forEach((id) => {
      const item = this.findIdInData(id, this.state.data);
      if (item) {
        this.props.deleteItem(this.props.mkImmutableItem(item));
      }
    });
    return true;
  };

  onDelete = (items) => {
    if (items.length === 1) return this.executeDeleteItems(items);

    const es = items.length > 1 ? 's' : '';
    this.props.dialogAlert(
      `Delete ${items.length} Item${es}?`,
      `You are about to delete ${items.length} item${es}.  Are you sure?`,
      (ret) => {
        if (ret.btnPressed === 'Yes') {
          return this.executeDeleteItems(items);
        }
        return true;
      },
      ['Cancel', 'Yes'],
    );
    return true;
  };

  // item returned with an id = 0 is a new item
  onSave = (data) => {
    const dupField = this.checkForDuplicates(this.state.data, this.props.dataDictionary, data);

    if (dupField) {
      this.props.dialogAlert(
        'That item already exists!',
        `Sorry, but that item already exists, you need to use a unique ${dupField}.`,
        () => {},
        ['Got It'],
      );
    } else if (data.id === 0) {
      const newId = uuidv4().toUpperCase();
      this.props.createItem(this.props.mkImmutableItem({ ...data, id: null, clientId: newId }));
    } else {
      this.props.updateItem(this.props.mkImmutableItem({ ...data }));
    }
  };

  compareFields = (rec, item, fldRecord) => {
    if (rec.values && rec.values[fldRecord.id]) {
      return deepEqual(rec.values[fldRecord.id], item.values[fldRecord.id]);
    }

    return rec[fldRecord.id] === item[fldRecord.id];
  };

  checkForDuplicates = (data, dataDictionary, item) => {
    const uniqueFields = dataDictionary.filter((x) => x.unique || x.uniqueWith);
    let dupeField = null;

    if (uniqueFields) {
      //
      // check every record that was provided looking for a duplicate
      //
      data.forEach((rec) => {
        //
        // check each field that was marked unique
        //

        let oneDupeField = null;
        uniqueFields.forEach((fld) => {
          if (rec[fld.id] === item[fld.id] && rec.id !== item.id) {
            oneDupeField = fld.id;
          }
          // if uniqueWith is being used, then it must also match the fields in the uniqueWith array
          // run through the dups found
          if (oneDupeField && fld.uniqueWith) {
            // start by assuming they will all match
            let withFieldsMatched = true;
            // check each 'with' field from the data dictionary
            fld.uniqueWith.forEach((fldId) => {
              const withField = dataDictionary.find((f) => f.id === fldId);

              if (!this.compareFields(rec, item, withField)) {
                // if one does not match, this overall is not a match
                withFieldsMatched = false;
              }
            });
            if (!withFieldsMatched) {
              oneDupeField = null;
            }
          }
        });

        dupeField = oneDupeField || dupeField;

        // recursion for children
        if (rec.children && !dupeField) {
          dupeField = dupeField || this.checkForDuplicates(rec.children, dataDictionary, item);
        }
      });
    }
    return dupeField;
  };

  render() {
    const { classes, theme, dataDictionary, title, titleIconName, settingType, ...other } = this.props;
    const enableListSettings = !this.props.underConstruction;

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <div className={classes.root}>
            <div>
              <Grid container>
                <Grid item xs>
                  {!enableListSettings && (
                    <>
                      <SectionTitle>
                        <Typography variant='displayRegular'>{title}</Typography>
                      </SectionTitle>
                      <div className={classes.underConstructionContainer}>
                        <br />
                        <br />
                        <img alt='page is under construction' src={underConstruction} style={{ width: '80%' }} />
                      </div>
                    </>
                  )}

                  {enableListSettings && (
                    <>
                      <div className={classes.sectionTitle}>
                        <div className={classes.navMenuItem}>{icons[titleIconName]}</div>
                        <Typography variant='headlineRegular' className={classes.titleText}>
                          {title}
                        </Typography>
                      </div>
                      <QList
                        {...other}
                        data={this.state.data.toJS()}
                        dataDictionary={dataDictionary.toJS()}
                        defaultSort={dataDictionary.first().id}
                        detailsComponent={QListDetails}
                        onSave={this.onSave}
                        onDelete={this.onDelete}
                        listItemType={settingType}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </div>
          </div>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

SettingsItems.defaultProps = {
  titleIconName: 'settings',
  underConstruction: false,
};

SettingsItems.propTypes = {
  getItems: PropTypes.func,
  createItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
  deleteItems: PropTypes.func,
  settingType: PropTypes.string,
  title: PropTypes.string,
  titleIconName: PropTypes.string,
  items: PropTypes.object,
  mkImmutableItem: PropTypes.func,
  mkItemArray: PropTypes.func,
  dataDictionary: PropTypes.object,
  underConstruction: PropTypes.bool,
  titleIconBackground: PropTypes.string,

  classes: PropTypes.any,
  theme: PropTypes.any,

  dialogAlert: PropTypes.func,
};

export default compose(withTheme, QDialogs())(withStyles(SettingsItems, styles, { name: { SettingsItems } }));
